import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/components/ProtectedRoute";
import { toast } from "sonner";
import { Loader2, Upload, CheckCircle2, ImageIcon } from "lucide-react";

export const PaymentProofForm = ({ email = "", phone = "", lockIdentity = false, onDone }) => {
  const [form, setForm] = useState({ email, phone, transaction_id: "" });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.phone) {
      toast.error("Email and phone are required.");
      return;
    }
    if (!form.transaction_id.trim() && !file) {
      toast.error("Enter a transaction ID or attach a screenshot.");
      return;
    }
    const fd = new FormData();
    fd.append("email", form.email);
    fd.append("phone", form.phone);
    fd.append("transaction_id", form.transaction_id);
    if (file) fd.append("screenshot", file);
    setLoading(true);
    try {
      await api.post("/payment-proof", fd);
      setSent(true);
      toast.success("Payment proof submitted!");
      onDone?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit proof");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="text-center py-4" data-testid="payment-proof-success">
        <CheckCircle2 className="w-10 h-10 text-green-400 mx-auto mb-3" />
        <p className="text-amber-50 font-600">Proof received — thank you!</p>
        <p className="text-sm text-zinc-400 mt-1">Our team will verify and confirm your payment shortly.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4 text-left" data-testid="payment-proof-form">
      {!lockIdentity && (
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-zinc-300">Registered Email *</Label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              className="mt-1.5 bg-[#18181F] border-amber-500/20"
              placeholder="you@email.com"
              data-testid="proof-email-input"
            />
          </div>
          <div>
            <Label className="text-zinc-300">Registered Phone *</Label>
            <Input
              value={form.phone}
              onChange={(e) => set("phone", e.target.value)}
              className="mt-1.5 bg-[#18181F] border-amber-500/20"
              placeholder="+91 ..."
              data-testid="proof-phone-input"
            />
          </div>
        </div>
      )}
      <div>
        <Label className="text-zinc-300">UPI Transaction ID / UTR</Label>
        <Input
          value={form.transaction_id}
          onChange={(e) => set("transaction_id", e.target.value)}
          className="mt-1.5 bg-[#18181F] border-amber-500/20 font-mono-lux"
          placeholder="e.g. T2610101234567890"
          data-testid="proof-transaction-id-input"
        />
      </div>
      <div>
        <Label className="text-zinc-300">Payment Screenshot (JPG/PNG, max 5 MB)</Label>
        <label
          className="mt-1.5 flex items-center gap-3 p-3 rounded-lg bg-[#18181F] gold-border cursor-pointer hover:bg-amber-500/5"
          data-testid="proof-screenshot-dropzone"
        >
          <ImageIcon className="w-5 h-5 text-amber-400 shrink-0" />
          <span className="text-sm text-zinc-300 truncate flex-1" data-testid="proof-screenshot-filename">
            {file ? file.name : "Choose screenshot..."}
          </span>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="proof-screenshot-input"
          />
        </label>
      </div>
      <button
        type="submit"
        disabled={loading}
        className="w-full gold-gradient-bg text-black font-700 py-3 rounded-full hover:brightness-110 transition-all flex items-center justify-center gap-2 disabled:opacity-60"
        data-testid="proof-submit-button"
      >
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Upload className="w-4 h-4" /> Submit Payment Proof</>}
      </button>
    </form>
  );
};
